import React from 'react'
import { createRoot } from 'react-dom/client'
import { mixpanelInit } from '@components/Analytics/mixpanel'
import { setDlogErrorLogger, setDlogInfoLogger, setDlogWarnLogger } from '@towns-protocol/utils'
import debug from 'debug'
import { Analytics } from 'hooks/useAnalytics'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import { Main } from 'Main'
import { env } from 'utils'
import { bufferedLogger } from 'utils/wrappedlogger'

import 'allotment/dist/style.css'
import 'index.css'

import { initDatadog } from './datadog'
import { featureFlags } from './featureFlags'

// Timeout in seconds for LaunchDarkly SDK initialization
const LAUNCHDARKLY_INIT_TIMEOUT_SECONDS = 10

console.log = bufferedLogger.getLogger().info
console.info = bufferedLogger.getLogger().info
console.warn = bufferedLogger.getLogger().warn
console.debug = bufferedLogger.getLogger().debug
console.error = bufferedLogger.getLogger().error
debug.log = bufferedLogger.getLogger().debugLog

setDlogErrorLogger(bufferedLogger.getLogger().error)
setDlogWarnLogger(bufferedLogger.getLogger().warn)
setDlogInfoLogger(bufferedLogger.getLogger().info)

if (typeof window !== 'undefined') {
    // Expose buffered logger functions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).getLogBuffer = () => {
        return bufferedLogger.getBufferAsString()
    }
}

// Enhanced error capturing
const setupErrorCapture = () => {
    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        const error = event.reason
        let errorMessage = 'Unhandled Promise Rejection: '

        if (error instanceof Error) {
            errorMessage += `${error.name}: ${error.message}\n${error.stack}`
        } else if (typeof error === 'string') {
            errorMessage += error
        } else {
            errorMessage += JSON.stringify(error)
        }

        console.error(errorMessage)
        bufferedLogger.getLogger().error(errorMessage)
    })

    // Capture general window errors
    window.addEventListener('error', (event) => {
        const errorMessage = `Window Error: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`
        console.error(errorMessage)
        bufferedLogger.getLogger().error(errorMessage)
    })
}

// Call the setup function
setupErrorCapture()

const originalLanguage = document.documentElement.lang

const observer = new MutationObserver(() => {
    const lang = document.documentElement.lang
    if (lang !== originalLanguage) {
        console.log(`🌎 Page translation detected, lang="${lang}" (original="${originalLanguage}")`)
    }
})
observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['lang'],
    childList: false,
    characterData: false,
})

console.log(
    `%c\n\nTOWNS\n%c${VITE_APP_MODE}\n${VITE_APP_COMMIT_HASH}\n${new Date(
        VITE_APP_TIMESTAMP,
    ).toLocaleString()}\n${env.DEV ? 'DEV' : ''}${'\n'.repeat(3)}`,
    `font-size:32px;font-weight:bold;font-family:sans-serif;`,
    ``,
)

// fixes: uncaught ReferenceError: global is not defined for 3rd party libs
window.global ||= globalThis

if (env.DEV) {
    // Register runtime-error overlay
    // From: https://github.com/vitejs/vite/issues/2076
    const showErrorOverlay = (event: ErrorEvent) => {
        // must be within function call because that's when the element is defined for sure.
        const ErrorOverlay = customElements.get('vite-error-overlay')
        // don't open outside vite environment
        if (!ErrorOverlay) {
            return
        }
        const error = event.error || event
        if (!error) {
            console.error('no error found in event', event)
            return
        }
        console.log('showErrorOverlay:', error)

        if (document.querySelector('vite-error-overlay') || env.VITE_DISABLE_ERROR_OVERLAY) {
            // avoid stacking error overlays
            return
        }
        const overlay = new ErrorOverlay(error)
        document.body.appendChild(overlay)
    }
    window.addEventListener('error', showErrorOverlay)
    window.addEventListener('unhandledrejection', ({ reason }) => showErrorOverlay(reason))
}

initDatadog()

const node = document.getElementById('root')

if (!node) {
    // this would happen if HTML is malformed or absent
    throw new Error('no root node enable')
}

async function enableMocking() {
    console.log('Starting MSW...')
    const { worker } = await import('../mocks/browser')
    if ('start' in worker && typeof worker.start === 'function') {
        // once the Service Worker is up and ready to intercept requests.
        return worker.start()
    }
}

const analytics = Analytics.getInstance()
const commonProperties = analytics.getCommonProperties()

mixpanelInit()

const MainWithLaunchDarkly = env.VITE_LAUNCHDARKLY_CLIENT_ID
    ? withLDProvider({
          clientSideID: env.VITE_LAUNCHDARKLY_CLIENT_ID ?? '',
          flags: featureFlags,
          reactOptions: {
              useCamelCaseFlagKeys: true,
          },
          options: {
              streaming: true,
              bootstrap: 'localStorage',
          },
          timeout: LAUNCHDARKLY_INIT_TIMEOUT_SECONDS,
          context: {
              kind: 'user',
              key: analytics.pseudoId || 'anonymous',
              anonymous: !analytics.pseudoId,
              traits: {
                  browserName: commonProperties.browserName,
                  device: commonProperties.device,
                  platform: commonProperties.platform,
                  release: commonProperties.release,
              },
          },
      })(Main)
    : Main

if (env.DEV && env.VITE_ENABLE_MSW_BROWSER) {
    const logLine = `🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨 BROWSER MSW ENABLED, SOME REQUESTS MAY BE MOCKED 🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨`
    console.log(logLine)
    setInterval(() => console.log(logLine), 10_000)
    enableMocking().then(() => {
        createRoot(node).render(<MainWithLaunchDarkly />)
    })
} else {
    createRoot(node).render(<MainWithLaunchDarkly />)
}
